/**
 * UX de voz compartida: TTS + STT (Web Speech API).
 * Sin audio crudo; solo lectura y reconocimiento en el dispositivo.
 */

export function canSpeak() {
    return typeof window !== 'undefined' && 'speechSynthesis' in window;
}

export function speechRecognitionCtor() {
    if (typeof window === 'undefined') return null;
    return window.SpeechRecognition || window.webkitSpeechRecognition || null;
}

export function canListen() {
    return Boolean(speechRecognitionCtor());
}

export function cancelSpeech() {
    if (!canSpeak()) return;
    try {
        window.speechSynthesis.cancel();
    } catch {
        /* ignore */
    }
}

export function speakEs(text, enabled = true) {
    if (!enabled || !canSpeak() || !text) return;
    try {
        cancelSpeech();
        const u = new SpeechSynthesisUtterance(String(text).replace(/\n+/g, '. '));
        u.lang = 'es-CO';
        u.rate = 0.95;
        window.speechSynthesis.speak(u);
    } catch {
        /* ignore */
    }
}

export function stripAccents(s) {
    return String(s || '')
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .trim();
}

/** options: { key, label, voice?: string[] } */
export function matchVoiceOption(transcript, options) {
    const t = stripAccents(transcript);
    if (!t) return null;
    for (const opt of options) {
        const words = opt.voice || [stripAccents(opt.label), opt.key];
        if (words.some((w) => t.includes(stripAccents(w)))) return opt;
    }
    return null;
}

/** Convierte opciones de encuesta { value, label } a forma matchVoiceOption. */
export function quizOptionsForVoice(options) {
    return (options || []).map((opt) => {
        const label = String(opt.label || '');
        const tokens = label
            .split(/\s+/)
            .map((w) => w.replace(/[^\p{L}\p{N}]/gu, ''))
            .filter((w) => w.length >= 4);
        return {
            key: opt.value,
            label,
            voice: [label, String(opt.value), ...tokens],
        };
    });
}

/**
 * Una toma de reconocimiento. Devuelve { stop }.
 * onResult(transcript), onError(), onEnd().
 */
export function startOneShotListen({ lang = 'es-CO', onResult, onError, onEnd } = {}) {
    const Ctor = speechRecognitionCtor();
    if (!Ctor) {
        onError?.({ code: 'unsupported' });
        return { stop: () => {} };
    }
    cancelSpeech();
    const rec = new Ctor();
    rec.lang = lang;
    rec.interimResults = false;
    rec.maxAlternatives = 1;
    rec.onresult = (ev) => {
        const said = ev.results?.[0]?.[0]?.transcript || '';
        onResult?.(said);
    };
    rec.onerror = () => onError?.();
    rec.onend = () => onEnd?.();
    try {
        rec.start();
    } catch (err) {
        onError?.(err);
    }
    return {
        stop: () => {
            try {
                rec.abort?.();
            } catch {
                /* ignore */
            }
        },
    };
}
